import Keycloak from "keycloak-js";

export class LeanbotAuth {
  static #config = null;
  static #keycloak = null;
  static #refreshTimer = null;
  static #refreshInFlight = false;
  static #flow = "implicit";

  static setConfig(config) {
    LeanbotAuth.#config = config || null;
  }

  static isEnabled() {
    return Boolean(LeanbotAuth.#config?.Enabled);
  }

  static getAccessToken() {
    if (LeanbotAuth.#keycloak?.idToken) return LeanbotAuth.#keycloak.idToken;
    return localStorage.getItem("token") || "";
  }

  // p2026 #616: the ACCESS token (the account guard checks its azp and issuer).
  static getKeycloakAccessToken() {
    return LeanbotAuth.#keycloak?.token || localStorage.getItem("kc_token") || "";
  }

  static getAuthHeaders() {
    const token = LeanbotAuth.getAccessToken();
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }

  static async init() {
    if (!LeanbotAuth.isEnabled()) return;

    const keycloakConfig = LeanbotAuth.#config;
    const hasRequiredConfig =
      keycloakConfig?.url &&
      keycloakConfig?.realm &&
      keycloakConfig?.clientId;

    if (!hasRequiredConfig) {
      throw new Error("Keycloak config is missing required fields: url, realm, clientId");
    }

    LeanbotAuth.#keycloak = new Keycloak({
      url: keycloakConfig.url,
      realm: keycloakConfig.realm,
      clientId: keycloakConfig.clientId,
    });

    LeanbotAuth.#flow = keycloakConfig?.flow || "implicit";
    const authenticated = await LeanbotAuth.#keycloak.init({
      onLoad: "login-required",
      flow: LeanbotAuth.#flow,
      checkLoginIframe: false,
    });

    if (!authenticated) {
      throw new Error("Keycloak authentication failed");
    }

    LeanbotAuth.#persistIdentity();
    LeanbotAuth.#startRefreshLoop();
  }

  // Leanbot toolkit bar: name + login for the shared user menu (no token leaves this class).
  static getUserProfile() {
    const p = LeanbotAuth.#keycloak?.idTokenParsed;
    if (!p) return null;
    return { name: p.name || p.preferred_username || "", login: p.preferred_username || "" };
  }

  static async logout(options) {
    if (LeanbotAuth.#refreshTimer) {
      clearTimeout(LeanbotAuth.#refreshTimer);
      LeanbotAuth.#refreshTimer = null;
    }
    // #395: clear the persisted identity BEFORE redirecting. keycloak.logout()
    // navigates away, but localStorage survives the round trip -- and `kc_token`
    // is now a live access token (realm `idp` issues 7200s ones), so leaving it
    // behind hands the next person on a shared machine a credential that any
    // offline validator still accepts. The gateway itself rejects it once the KC
    // session dies, which is the point of #395, but nothing else has to.
    LeanbotAuth.#clearPersistedIdentity();
    if (!LeanbotAuth.#keycloak) return;
    await LeanbotAuth.#keycloak.logout(options);
  }

  static #clearPersistedIdentity() {
    try {
      localStorage.removeItem("kc_token");
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      delete localStorage.token;
    } catch (error) {
      console.warn("Could not clear persisted identity:", error);
    }
  }

  static #persistIdentity() {
    const username = LeanbotAuth.#keycloak?.idTokenParsed?.preferred_username || "";
    const token = LeanbotAuth.#keycloak?.idToken || "";

    // #395: the WS gateway validates via Keycloak userinfo, which requires the
    // ACCESS token (the ID token fails and the socket counts as anonymous).
    // mqtt-gateway-client reads `kc_token` first, so persist it there; the
    // legacy `token` key (ID token) stays untouched for existing consumers.
    const accessToken = LeanbotAuth.#keycloak?.token || "";
    if (accessToken) {
      localStorage.setItem("kc_token", accessToken);
    }

    localStorage.setItem("username", username);
    localStorage.setItem("token", token);
    localStorage.token = token;
  }

  // Token refresh (standard flow only; the implicit flow has no refresh token).
  // IDE/IDE2 perf review for the 2,000-student test: the old loop retried a
  // failed refresh every 5 s, forever, in every tab, all in step. With the eID
  // slow or down that is 400 POSTs/s to the token endpoint from 2,000 tabs; after
  // the session ended (refresh token rejected, keycloak-js clears it) it spun
  // every 5 s on "no refresh token"; and a PC whose clock runs ahead of the eID
  // saw its token as "about to expire" and woke every 5 s. Now:
  // - the expiry check uses keycloak-js's timeSkew, as updateToken() does;
  // - a failed refresh backs off 5 s, 10 s, 20 s ... up to 5 min, jittered
  //   0.5x-1.5x, and the first success goes back to the normal schedule;
  // - no refresh token left = stop (keycloak-js starts the sign-in itself).
  static REFRESH = Object.freeze({
    MIN_VALIDITY_SECONDS: 30,
    MIN_DELAY_MS: 5000,
    MAX_DELAY_MS: 60000,
    BACKOFF_MAX_MS: 300000,
  });

  static #startRefreshLoop() {
    if (LeanbotAuth.#refreshTimer) {
      clearTimeout(LeanbotAuth.#refreshTimer);
      LeanbotAuth.#refreshTimer = null;
    }

    // Keycloak implicit flow does not provide refresh token.
    // Skip updateToken() to avoid repeated runtime errors.
    if (LeanbotAuth.#flow === "implicit" || !LeanbotAuth.#keycloak?.refreshToken) {
      return;
    }

    const { MIN_VALIDITY_SECONDS, MIN_DELAY_MS, MAX_DELAY_MS, BACKOFF_MAX_MS } = LeanbotAuth.REFRESH;
    let failures = 0;

    // Seconds the access token is still valid on the eID's clock (null: unknown).
    const secondsLeft = () => {
      const kc = LeanbotAuth.#keycloak;
      const exp = kc?.tokenParsed?.exp;
      if (!exp) return null;
      return exp - Date.now() / 1000 + (kc.timeSkew || 0);
    };

    const scheduleNext = () => {
      let delayMs;
      if (failures > 0) {
        const base = Math.min(BACKOFF_MAX_MS, MIN_DELAY_MS * 2 ** (failures - 1));
        delayMs = base * (0.5 + Math.random());
      } else {
        const left = secondsLeft();
        delayMs = left === null
          ? MIN_DELAY_MS
          : Math.max(MIN_DELAY_MS, Math.min(MAX_DELAY_MS, (left - MIN_VALIDITY_SECONDS) * 1000));
      }
      LeanbotAuth.#refreshTimer = setTimeout(refreshOnce, delayMs);
    };

    const stop = () => {
      LeanbotAuth.#refreshTimer = null;
      console.warn("Keycloak token refresh stopped: no refresh token (session ended).");
    };

    const refreshOnce = async () => {
      LeanbotAuth.#refreshTimer = null;
      const kc = LeanbotAuth.#keycloak;
      if (!kc) return;
      if (!kc.refreshToken) return stop();
      if (LeanbotAuth.#refreshInFlight) {
        scheduleNext();
        return;
      }

      LeanbotAuth.#refreshInFlight = true;
      try {
        const refreshed = await kc.updateToken(MIN_VALIDITY_SECONDS);
        failures = 0;
        if (refreshed) LeanbotAuth.#persistIdentity();
      } catch (error) {
        failures += 1;
        console.error("Keycloak token refresh failed:", error);
      } finally {
        LeanbotAuth.#refreshInFlight = false;
      }
      if (!kc.refreshToken) return stop();
      scheduleNext();
    };

    scheduleNext();
  }
}
